/**
 * The one place a badge tone is validated and normalised.
 *
 * Badge colours used to live in hardcoded maps keyed on a code (`HIGH`,
 * `URGENT`, ...). That silently un-coloured every value a tenant created for
 * itself, and duplicated data the database already holds: `dbo.TenantLookup`
 * has `BadgeStyle` and `core.EntityStatus` has `BadgeStyle`. Configs can now
 * point a badge straight at a field holding that value.
 *
 * A tone arriving from the database is untrusted input. It is matched against
 * the table below and never concatenated into a class name unchecked, so a
 * junk row cannot inject an arbitrary CSS class into the page.
 *
 * ## The allowlist
 *
 * `backend/Solo.Tenant.DB/Scripts/Data/Lookups/BADGE_STYLE.sql` is the source
 * of truth. It seeds the `BADGE_STYLE` lookup, which is the picker a tenant
 * chooses a tone from on the lookup admin pages, and it holds seven names:
 *
 *   primary | secondary | tertiary | success | danger | warning | info
 *
 * {@link BADGE_TONES} is exactly that set — `badge-tone.spec.ts` reads the seed
 * file and asserts the two agree in both directions. Each tone has a
 * `.badge-*` rule (search grid, overview mini-grid, checklist) and a mapping to
 * an Ionic colour for the chip renderers.
 *
 * ## Retired names
 *
 * The aliases below are names the picker no longer offers. They exist so a row
 * written before the name was retired still renders a badge rather than going
 * bare — never to make the name selectable again.
 *
 * `light` and `dark` were offerable but never paintable: they are *surface*
 * tones, not status tones. Solo repoints `--ion-color-dark` to a near-white
 * surface (`#F3F1E9` in the light theme), so a chip in `dark` is
 * white-on-white, and a third and fourth near-neutral grey would be
 * indistinguishable from `secondary` anyway. Both always folded to neutral, so
 * an option could not look like itself — the same bug `medium` caused from the
 * other direction. Existing rows are repointed to `secondary` (the tone they
 * already rendered as) by `Scripts/Migrations/RetireUnpaintableBadgeStyles.sql`.
 *
 * `medium` was never in the lookup at all (it is from Ionic's palette, not this
 * family). Seeds that shipped with it are corrected, and existing rows are
 * repaired by `Scripts/Migrations/FixCaseBadgeStylesNotInAllowlist.sql`.
 */
export type SoloBadgeTone = 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'danger' | 'info';

/** The tones this renderer paints. Must match the BADGE_STYLE lookup exactly. */
export const BADGE_TONES: readonly SoloBadgeTone[] =
  ['primary', 'secondary', 'tertiary', 'success', 'warning', 'danger', 'info'];

/**
 * Used when a tone is missing (the column is nullable, and rows in other apps
 * are NULL today) or unrecognised. A badge must never fall back to *no*
 * colour — an unstyled badge reads as a rendering bug, not as "no data". The
 * lookup picker agrees: a stored value it does not recognise selects None.
 */
export const NEUTRAL_BADGE_TONE: SoloBadgeTone = 'secondary';

/**
 * Every accepted spelling → the tone it paints. Keys are lowercase; the
 * `badge-` prefix is stripped before lookup, so both `warning` (how the DB
 * stores it) and `badge-warning` (how existing config maps spell it) resolve.
 */
const BADGE_TONE_ALIASES: Readonly<Record<string, SoloBadgeTone>> = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
  // Retired from the allowlist — see "Retired names" in the file comment.
  // Retained so a row written before the retirement still renders rather than
  // going bare; the picker no longer offers any of them.
  light: 'secondary',
  dark: 'secondary',
  medium: 'secondary'
};

/** Tone → the `ion-*` colour name `ion-chip`/`ion-badge` `[color]` understands. */
const IONIC_COLOR_FOR_TONE: Readonly<Record<SoloBadgeTone, string>> = {
  primary: 'primary',
  // Solo's neutral. Ionic `medium` is the palette entry ThemeService overrides
  // per theme (#5C6A60 light / #B9C2BB dark), so it tracks the theme correctly.
  secondary: 'medium',
  tertiary: 'tertiary',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  // Solo's `info` blue has no Ionic slot of its own; Ionic `secondary` is the
  // blue in that palette, and `tertiary` above keeps the indigo, so the two
  // stay visually distinct. `.badge-info` already resolves to
  // `--ion-color-tertiary` in checklist-template for the same reason.
  info: 'secondary'
};

/**
 * Normalises any raw value to a paintable tone. Returns
 * {@link NEUTRAL_BADGE_TONE} for null, undefined, blank, non-string and
 * unrecognised values.
 */
export function resolveBadgeTone(raw: unknown): SoloBadgeTone {
  if (raw === null || raw === undefined) {
    return NEUTRAL_BADGE_TONE;
  }
  const key = String(raw).trim().toLowerCase().replace(/^badge-/, '');
  return BADGE_TONE_ALIASES[key] ?? NEUTRAL_BADGE_TONE;
}

/**
 * CSS class for the renderers that colour a badge by class (search grid,
 * overview mini-grid, data panels). Returns a string primitive, so it is safe
 * to call from a template once per row.
 */
export function resolveBadgeClass(raw: unknown): string {
  return `badge-${resolveBadgeTone(raw)}`;
}

/**
 * Colour name for the renderers that colour a badge through Ionic's `[color]`
 * input (entity header chip, details widget `ion-badge`).
 */
export function resolveBadgeColor(raw: unknown): string {
  return IONIC_COLOR_FOR_TONE[resolveBadgeTone(raw)];
}
